// src/pages/users/business/ManageGroupsAndChargersViewOnly.jsx
import React, { useEffect, useState } from 'react';
import {
  Container, Row, Col, Card, Form, Collapse, Spinner, Badge
} from 'react-bootstrap';
import axios from 'axios';
import Sidebar from '../../../../Components/Sidebar';
import getEnvConfig from '../.././../../config';
import { fetchToken } from '../../../../Auth';

const GroupsAndChargersViewer = () => {
  // Locations -> Groups -> Chargers -> Ports
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [chargers, setChargers] = useState([]);
  const [portsByChargerId, setPortsByChargerId] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingSection, setLoadingSection] = useState(null); // 'locations' | 'groups' | 'chargers' | null
  const [error, setError] = useState(null);

  const token = fetchToken();
  const { backendUrl } = getEnvConfig();

  const withLoading = async (section, fn) => {
    setLoading(true);
    setLoadingSection(section);
    setError(null);
    try {
      await fn();
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
      setLoadingSection(null);
    }
  };

  // Property Managers should only see their own data, so we DO NOT ask for username/email.
  useEffect(() => {
    if (!token || !backendUrl) return;
    withLoading('locations', async () => {
      const res = await axios.post(`${backendUrl}/chargers/get_locations_user`, {
        user_token: token,
      });
      setLocations(res.data.locations || []);
    });
  }, [backendUrl, token]);

  const handleLocationChange = async (e) => {
    const locationId = e.target.value;
    setSelectedLocationId(locationId);
    setSelectedGroupId(null);
    setGroups([]);
    setChargers([]);
    setPortsByChargerId({});

    if (!locationId || locationId === 'placeholder') return;

    await withLoading('groups', async () => {
      const res = await axios.post(`${backendUrl}/chargers/get_groups_user`, {
        location_id: locationId,
        user_token: token,
      });
      setGroups(res.data.groups || []);
    });
  };

  const handleGroupToggle = async (groupId) => {
    // Collapse if already open
    if (selectedGroupId === groupId) {
      setSelectedGroupId(null);
      setChargers([]);
      setPortsByChargerId({});
      return;
    }

    setSelectedGroupId(groupId);
    setChargers([]);
    setPortsByChargerId({});

    await withLoading('chargers', async () => {
      const res = await axios.post(`${backendUrl}/chargers/get_chargers_user`, {
        group_id: groupId,
        user_token: token,
      });
      const fetched = res.data.chargers || [];
      setChargers(fetched);

      // Fetch ports for each charger in parallel
      if (fetched.length) {
        const detailResponses = await Promise.all(
          fetched.map((ch) => axios.get(`${backendUrl}/chargers/${ch.id}/details`))
        );
        const map = {};
        detailResponses.forEach((resp, idx) => {
          const chId = fetched[idx].id;
          map[chId] = resp?.data?.charger?.ports || [];
        });
        setPortsByChargerId(map);
      }
    });
  };

  const statusBadgeColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'available') return 'success';
    if (s === 'charging') return 'primary';
    if (s === 'error') return 'danger';
    return 'secondary';
  };

  const Empty = ({ children }) => (
    <div className="text-muted fst-italic">{children}</div>
  );

  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <Container fluid style={{ marginLeft: '5px' }}>
        <Row className="justify-content-center my-5">
          <Col md={10}>
            <h2 className="mb-4 text-center">Deployment Overview (View Only)</h2>

            {/* Location selector */}
            <Form.Group controlId="locationSelect" className="mb-4">
              <Form.Label>Select Location</Form.Label>
              <Form.Control
                as="select"
                value={selectedLocationId || 'placeholder'}
                onChange={handleLocationChange}
              >
                <option value="placeholder">Select a location...</option>
                {(locations || []).map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.address}, {location.city}, {location.state}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            {loading && (
              <div className="my-3 d-flex align-items-center">
                <Spinner animation="border" role="status" size="sm" className="me-2" />
                <span>
                  Loading {loadingSection || 'data'}…
                </span>
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Groups */}
            {!loading && selectedLocationId && groups.length === 0 && (
              <Empty>No groups found for the selected location.</Empty>
            )}

            {groups.map((group) => (
              <Card key={group.id} className="mb-3">
                <Card.Body>
                  <Card.Title className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fw-semibold">{group.group_name || 'Unnamed Group'}</span>
                    <Badge bg="secondary">Group ID: {group.id}</Badge>
                  </Card.Title>

                  <Row className="gy-2">
                    <Col md={4}>
                      <div className="text-muted">Total Max Amps</div>
                      <div className="fw-semibold">{group.total_max_amp ?? '—'}</div>
                    </Col>
                    <Col md={4}>
                      <div className="text-muted">Utility ID</div>
                      <div className="fw-semibold">{group.utility_id || '—'}</div>
                    </Col>
                    <Col md={4}>
                      <div className="text-muted">&nbsp;</div>
                      <Form.Text
                        role="button"
                        className="text-primary"
                        onClick={() => handleGroupToggle(group.id)}
                      >
                        {selectedGroupId === group.id ? 'Hide Chargers' : 'View Chargers'}
                      </Form.Text>
                    </Col>
                  </Row>

                  <Collapse in={selectedGroupId === group.id}>
                    <div className="pt-3">
                      {!loading && selectedGroupId === group.id && chargers.length === 0 && (
                        <Empty>No chargers in this group.</Empty>
                      )}

                      {chargers.map((charger) => (
                        <Card key={charger.id} className="mb-2">
                          <Card.Body>
                            <Row className="gy-2">
                              <Col md={6}>
                                <div className="text-muted">Charger ID</div>
                                <div className="fw-semibold">{charger.id}</div>
                              </Col>
                              <Col md={6}>
                                <div className="text-muted">UUID</div>
                                <div className="fw-semibold">{charger.uuid || '—'}</div>
                              </Col>

                              <Col md={4}>
                                <div className="text-muted">SIM ICCID</div>
                                <div className="fw-semibold">{charger.sim_iccid || '—'}</div>
                              </Col>
                              <Col md={4}>
                                <div className="text-muted">Price per kWh</div>
                                <div className="fw-semibold">
                                  {charger.price_per_kwh ?? '—'}
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-muted">Charging Rate (kW)</div>
                                <div className="fw-semibold">{charger.charging_rate ?? '—'}</div>
                              </Col>
                              <Col md={4}>
                                <div className="text-muted">Charging Amps</div>
                                <div className="fw-semibold">{charger.charger_amps ?? '—'}</div>
                              </Col>
                              <Col md={4}>
                                <div className="text-muted">Group ID</div>
                                <div className="fw-semibold">{charger.group_id ?? '—'}</div>
                              </Col>

                              {/* Negotiated rates (read-only) */}
                              {charger.negotiated_rates && (
                                <Col xs={12} className="pt-3">
                                  <Card className="border-0 bg-light">
                                    <Card.Body className="py-2">
                                      <div className="fw-semibold mb-2">Negotiated Rates</div>
                                      <Row className="gy-2">
                                        <Col md={4}>
                                          <div className="text-muted">Fixed Markup</div>
                                          <div className="fw-semibold">
                                            {charger.negotiated_rates.fixed_markup ?? '—'}
                                          </div>
                                        </Col>
                                        <Col md={4}>
                                          <div className="text-muted">Owner %</div>
                                          <div className="fw-semibold">
                                            {charger.negotiated_rates.owner_percentage ?? '—'}
                                          </div>
                                        </Col>
                                        <Col md={4}>
                                          <div className="text-muted">EV Owner %</div>
                                          <div className="fw-semibold">
                                            {charger.negotiated_rates.ev_owner_percentage ?? '—'}
                                          </div>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                </Col>
                              )}

                              {/* Ports (read-only) */}
                              <Col xs={12} className="pt-3">
                                <div className="fw-semibold mb-2">Ports</div>
                                {(portsByChargerId[charger.id] || []).length === 0 ? (
                                  <Empty>No ports reported for this charger.</Empty>
                                ) : (
                                  (portsByChargerId[charger.id] || []).map((port) => (
                                    <Card key={port.id} className="mb-2">
                                      <Card.Body className="py-3">
                                        <Row className="gy-2 align-items-center">
                                          <Col md={2}>
                                            <div className="text-muted">Port #</div>
                                            <div className="fw-semibold">{port.port_number ?? '—'}</div>
                                          </Col>
                                          <Col md={3}>
                                            <div className="text-muted">Connector Type</div>
                                            <div className="fw-semibold">{port.connector_type_name || '—'}</div>
                                          </Col>
                                          <Col md={3}>
                                            <div className="text-muted">Max Power (kW)</div>
                                            <div className="fw-semibold">{port.max_power_kw ?? '—'}</div>
                                          </Col>
                                          <Col md={2}>
                                            <div className="text-muted">Status</div>
                                            <div>
                                              <Badge bg={statusBadgeColor(port.status)}>
                                                {port.status || 'Unknown'}
                                              </Badge>
                                            </div>
                                          </Col>
                                          <Col md={2}>
                                            <div className="text-muted">Port UUID</div>
                                            <div className="fw-semibold">{port.uuid || '—'}</div>
                                          </Col>
                                          {port.qr_code && (
                                            <Col xs={12} className="pt-2">
                                              <img
                                                src={`data:image/png;base64,${port.qr_code}`}
                                                alt={`Port ${port.port_number} QR Code`}
                                                style={{ maxWidth: 120 }}
                                              />
                                            </Col>
                                          )}
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  ))
                                )}
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </Collapse>
                </Card.Body>
              </Card>
            ))}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default GroupsAndChargersViewer;
